import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Pipeline, AccessMode, AccessType } from '../interfaces/pipeline.model';
import { environment } from 'src/environments/environment';
import { CompanyService } from '@core/services/company.service';

@Injectable({
  providedIn: 'root',
})
export class PipelineApiService {
  private readonly baseUrl = `${environment.PIPELINE_BFF_URL}/pipeline`;

  constructor(
    private http: HttpClient,
    private companyService: CompanyService,
  ) {}

  private getCompaniesIds(): string[] {
    return (this.companyService.getCompanies() || []).map(c => c.companyId);
  }

  findByUser(language: string): Observable<Pipeline[]> {
    return this.http.get<Pipeline[]>(this.baseUrl, {
      params: { language, companiesIds: this.getCompaniesIds().join(',') },
    });
  }

  findDefaults(language: string): Observable<Pipeline[]> {
    return this.http.get<Pipeline[]>(`${this.baseUrl}/defaults`, {
      params: { language, companiesIds: this.getCompaniesIds().join(',') },
    });
  }

  findById(id: string): Observable<Pipeline> {
    return this.http.get<Pipeline>(`${this.baseUrl}/${id}`, {
      params: { companiesIds: this.getCompaniesIds().join(',') },
    });
  }

  create(pipeline: Partial<Pipeline>): Observable<Pipeline> {
    const { companiesIds, ...rest } = pipeline;
    return this.http.post<Pipeline>(this.baseUrl, {
      ...rest,
      boardCompaniesIds: companiesIds,
      companiesIds: this.getCompaniesIds(),
    });
  }

  update(id: string, pipeline: Partial<Pipeline>): Observable<Pipeline> {
    const { companiesIds, ...rest } = pipeline;
    return this.http.put<Pipeline>(`${this.baseUrl}/${id}`, {
      ...rest,
      boardCompaniesIds: companiesIds,
      companiesIds: this.getCompaniesIds(),
    });
  }

  duplicate(pipeline: Pipeline, clearTags = false): Observable<Pipeline> {
    return this.http.post<Pipeline>(`${this.baseUrl}/${pipeline.id}/duplicate`, {
      boardCompaniesIds: pipeline.companiesIds || [],
      companiesIds: this.getCompaniesIds(),
      clearTags,
    });
  }

  delete(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.baseUrl}/${id}`, {
      params: { companiesIds: this.getCompaniesIds().join(',') },
    });
  }

  updateAccess(
    id: string,
    viewAccess?: AccessMode,
    editAccess?: AccessMode,
  ): Observable<Pipeline> {
    return this.http.put<Pipeline>(`${this.baseUrl}/${id}/access`, {
      viewAccess,
      editAccess,
      companiesIds: this.getCompaniesIds(),
    });
  }

  share(
    id: string,
    userIds: number[],
    accessType: AccessType,
  ): Observable<{ shared: boolean }> {
    return this.http.post<{ shared: boolean }>(`${this.baseUrl}/${id}/share`, {
      userIds,
      accessType,
      companiesIds: this.getCompaniesIds(),
    });
  }

  removeShare(
    pipelineId: string,
    userId: number,
  ): Observable<{ removed: boolean }> {
    return this.http.delete<{ removed: boolean }>(
      `${this.baseUrl}/${pipelineId}/share/${userId}`,
      {
        params: { companiesIds: this.getCompaniesIds().join(',') },
      },
    );
  }
}
